import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Activity, ArrowLeft, UserPlus } from "lucide-react";
import type { Patient, PatientFormData } from "../../types";

interface PatientFormBaseProps {
  mode: "create" | "update";
  initialData?: Patient | null;
  onSubmit: (data: PatientFormData) => Promise<void>;
  isSubmitting?: boolean;
}

const PatientFormBase: React.FC<PatientFormBaseProps> = ({
  mode,
  initialData,
  onSubmit,
  isSubmitting = false,
}) => {
  const [formData, setFormData] = useState<PatientFormData>({
    firstName: initialData?.firstName || "",
    lastName: initialData?.lastName || "",
    username: initialData?.username || "",
    dateOfBirth: initialData?.dateOfBirth || "",
    gender: initialData?.gender || "",
    address: initialData?.address || "",
    phone: initialData?.phone || "",
    email: initialData?.email || "",
    role: "PATIENT",
  });

  const [errors, setErrors] = useState<Partial<PatientFormData>>({});

  const validateForm = (): boolean => {
    const newErrors: Partial<PatientFormData> = {};
    if (!formData.firstName) newErrors.firstName = "Le nom est requis";
    if (!formData.lastName) newErrors.lastName = "Le prénom est requis";
    if (!formData.username) newErrors.username = "Le nom d'utilisateur est requis";
    if (!formData.dateOfBirth) newErrors.dateOfBirth = "La date de naissance est requise";
    if (!formData.gender) newErrors.gender = "Le genre est requis";
    if (!formData.address) newErrors.address = "L'adresse est requise";
    if (!formData.phone) newErrors.phone = "Le téléphone est requis";
    if (!formData.email) newErrors.email = "L'email est requis";
    else if (!/\S+@\S+\.\S+/.test(formData.email)) newErrors.email = "L'email est invalide";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: undefined }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;
    await onSubmit(formData);
  };

  return (
    <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100 w-full max-w-2xl animate-slideInUp">
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-purple-600 rounded-xl flex items-center justify-center shadow-lg">
            <UserPlus className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-xl sm:text-2xl font-bold bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-transparent">
              {mode === "create" ? "Créer un compte patient" : "Mettre à jour le compte patient"}
            </h1>
            <p className="text-gray-600 text-sm">
              {mode === "create"
                ? "Ajoutez un nouveau patient à la plateforme."
                : "Modifiez les informations du patient."}
            </p>
          </div>
        </div>
        <Link
          to="/admin/dashboard/account/patient"
          className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-gray-500 to-gray-600 text-white rounded-lg hover:from-gray-600 hover:to-gray-700 transition-colors shadow-md"
          aria-label="Retour à la gestion des patients"
        >
          <ArrowLeft className="w-4 h-4" />
          <span className="text-sm font-medium">Retour</span>
        </Link>
      </div>

      <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <input type="hidden" name="role" value="PATIENT" />
        <div className="space-y-4">
          {[
            { name: "firstName", label: "Nom*", placeholder: "Votre nom" },
            { name: "lastName", label: "Prénom*", placeholder: "Votre prénom" },
            // { name: "username", label: "Nom d'utilisateur*", placeholder: "Votre nom d'utilisateur" },
            { name: "dateOfBirth", label: "Date de naissance*", type: "date" },
            {
              name: "gender",
              label: "Genre*",
              type: "select",
              options: [
                { value: "", label: "Sélectionnez votre genre" },
                { value: "M", label: "Masculin" },
                { value: "F", label: "Féminin" },
                { value: "O", label: "Autre" },
              ],
            },
          ].map((field) => (
            <div key={field.name}>
              <label htmlFor={field.name} className="block text-sm font-medium text-gray-700 mb-1">
                {field.label}
              </label>
              {field.type === "select" ? (
                <select
                  id={field.name}
                  name={field.name}
                  value={formData[field.name as keyof PatientFormData]}
                  onChange={handleChange}
                  className={`w-full px-4 py-2.5 border ${
                    errors[field.name as keyof PatientFormData] ? "border-red-500" : "border-gray-200"
                  } rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-gray-50 text-gray-800 outline-none transition-all`}
                  aria-required="true"
                >
                  {field.options?.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              ) : (
                <input
                  type={field.type || "text"}
                  id={field.name}
                  name={field.name}
                  value={formData[field.name as keyof PatientFormData]}
                  onChange={handleChange}
                  placeholder={field.placeholder}
                  className={`w-full px-4 py-2.5 border ${
                    errors[field.name as keyof PatientFormData] ? "border-red-500" : "border-gray-200"
                  } rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-gray-50 text-gray-800 placeholder-gray-500 outline-none transition-all truncate`}
                  aria-required="true"
                />
              )}
              {errors[field.name as keyof PatientFormData] && (
                <p className="mt-1 text-sm text-red-500">{errors[field.name as keyof PatientFormData]}</p>
              )}
            </div>
          ))}
        </div>
        <div className="space-y-4">
          {[
            { name: "email", label: "Email*", placeholder: "exemple@email.com", type: "email" },
            { name: "phone", label: "Téléphone*", placeholder: "+123 456 7890", type: "tel" },
            // {
            //   name: "password",
            //   label: mode === "create" ? "Mot de passe*" : "Mot de passe",
            //   placeholder: "••••••••",
            //   type: "password",
            // },
          ].map((field) => (
            <div key={field.name}>
              <label htmlFor={field.name} className="block text-sm font-medium text-gray-700 mb-1">
                {field.label}
              </label>
              <input
                type={field.type || "text"}
                id={field.name}
                name={field.name}
                value={formData[field.name as keyof PatientFormData]}
                onChange={handleChange}
                placeholder={field.placeholder}
                className={`w-full px-4 py-2.5 border ${
                  errors[field.name as keyof PatientFormData] ? "border-red-500" : "border-gray-200"
                } rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-gray-50 text-gray-800 placeholder-gray-500 outline-none transition-all truncate`}
                aria-required={field.name !== "password" || mode === "create"}
              />
              {errors[field.name as keyof PatientFormData] && (
                <p className="mt-1 text-sm text-red-500">{errors[field.name as keyof PatientFormData]}</p>
              )}
            </div>
          ))}
        </div>
        <div className="md:col-span-2">
          <label htmlFor="address" className="block text-sm font-medium text-gray-700 mb-1">
            Adresse*
          </label>
          <input
            type="text"
            id="address"
            name="address"
            value={formData.address}
            onChange={handleChange}
            placeholder="Votre adresse complète"
            className={`w-full px-4 py-2.5 border ${
              errors.address ? "border-red-500" : "border-gray-200"
            } rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-gray-50 text-gray-800 placeholder-gray-500 outline-none transition-all truncate`}
            aria-required="true"
          />
          {errors.address && <p className="mt-1 text-sm text-red-500">{errors.address}</p>}
        </div>
        <div className="md:col-span-2 flex justify-end gap-3">
          <Link
            to="/admin/dashboard/account/patient"
            className="px-4 py-2 border border-gray-200 bg-gray-50 rounded-lg text-gray-700 hover:bg-gray-100 text-sm font-medium"
          >
            Annuler
          </Link>
          <button
            type="submit"
            disabled={isSubmitting}
            className={`px-4 py-2 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-lg text-sm font-medium ${
              isSubmitting ? "opacity-50 cursor-not-allowed" : "hover:from-blue-600 hover:to-blue-700"
            } flex items-center justify-center`}
            aria-label={mode === "create" ? "Créer un compte patient" : "Enregistrer les modifications"}
          >
            {isSubmitting ? (
              <>
                <Activity className="w-4 h-4 mr-2 animate-spin" />
                Enregistrement...
              </>
            ) : (
              <>
                <UserPlus className="w-4 h-4 mr-2" />
                {mode === "create" ? "Créer le compte" : "Enregistrer les modifications"}
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default PatientFormBase;